// `/desktop-auth` (FR-A4): the approval page the packaged desktop app opens in the system
// browser. Outside the org shell on purpose — a desktop sign-in attempt has no org context of
// its own yet. Lives in the shared web bundle; the new-design-system PRs are not merged on
// staging, so this reuses the existing AuthShell/Button/Notice primitives like every other
// auth page (SignIn, SignUp, ...), not `@grackle/ui`.
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { DesktopAuthorizeResponse } from "../api/types";
import { useSession } from "../session";
import { AuthShell } from "../components/AuthShell";
import { Button, Notice, PageLoader } from "../components/ui";
import { DESKTOP_AUTH_RETURN_KEY } from "../components/DesktopAuthReturn";
import { parseDesktopAuthParams } from "./desktopAuthParams";

export default function DesktopAuthPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, orgs, loading } = useSession();
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  const result = parseDesktopAuthParams(location.search);

  useEffect(() => {
    if (!result.ok || loading || user) return;
    // Preserve the full return URL (state, code_challenge, redirect — everything) across
    // normal sign-in. `next` round-trips directly through the password/MFA path (SignIn.tsx);
    // `DesktopAuthReturn` is the fallback for Google/Microsoft/SSO, which redirect through the
    // server and can't carry a `next` query param through that round trip.
    const here = `${location.pathname}${location.search}`;
    sessionStorage.setItem(DESKTOP_AUTH_RETURN_KEY, here);
    navigate(`/signin?next=${encodeURIComponent(here)}`, { replace: true });
  }, [result.ok, loading, user, location, navigate]);

  if (!result.ok) {
    return (
      <AuthShell title="Can't open Grackle desktop" subtitle="">
        <Notice tone="error">{result.error}</Notice>
      </AuthShell>
    );
  }

  if (loading || !user) {
    return <PageLoader label="Checking your session…" />;
  }

  async function onApprove() {
    if (!result.ok) return;
    setApproving(true);
    setError(undefined);
    try {
      const res = await api<DesktopAuthorizeResponse>("POST", "/auth/desktop/authorize", {
        state: result.params.state,
        code_challenge: result.params.codeChallenge,
        code_challenge_method: result.params.codeChallengeMethod,
        redirect_uri: result.params.redirect,
      });
      sessionStorage.removeItem(DESKTOP_AUTH_RETURN_KEY);
      setRedirectTo(res.redirect_to);
      window.location.href = res.redirect_to;
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't approve this sign-in. Try again.");
    } finally {
      setApproving(false);
    }
  }

  const currentOrg = orgs[0];

  return (
    <AuthShell
      title="Open Grackle desktop"
      subtitle={
        currentOrg
          ? `Approve sign-in for ${user.email} (${currentOrg.name}).`
          : `Approve sign-in for ${user.email}.`
      }
    >
      {error ? <Notice tone="error">{error}</Notice> : null}
      {redirectTo ? (
        <Notice>
          Opening the Grackle desktop app… If nothing happens,{" "}
          <a href={redirectTo}>click here</a>.
        </Notice>
      ) : (
        <div className="auth-form">
          <Button block onClick={() => void onApprove()} loading={approving}>
            {approving ? "Approving…" : "Approve"}
          </Button>
          <Button type="button" variant="ghost" block onClick={() => navigate("/orgs")}>
            Cancel
          </Button>
        </div>
      )}
    </AuthShell>
  );
}
